import React, { useState, useEffect } from "react";
import { Spinner } from "@chakra-ui/react";
import { CustomModal } from "components/RFQ/Modal/CustomModal";
import { Button } from "newui/components/Button";
import type { Clarification } from "../../types/clarification.type";
import moment from "moment-timezone";
import { DateInputWithLabel, TimePickerWithLabel } from "newui/components/ui/InputWithLabel";
import FormikFieldError from "newui/components/ui/FormikFieldError";

interface SendConfirmationModalProps {
  clarifications: Clarification[];
  isLoading: boolean;
  isOpen: boolean;
  supplierName: string;
  onClose: () => void;
  onConfirm: (dateDue: string) => Promise<void>;
}

export const SendConfirmationModal: React.FC<SendConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  clarifications,
  supplierName,
  isLoading,
}) => {
  const [dateDue, setDateDue] = useState(moment(Date.now()).add(7, "days").toDate());
  const [timeDue, setTimeDue] = useState("5:00 PM");
  const [timezone, setTimezone] = useState(moment.tz.guess());
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      const defaultDate = moment()
        .add(7, "days")
        .toDate();
      setDateDue(defaultDate);
      setTimeDue("5:00 PM");
      setTimezone(moment.tz.guess());
      setError("");
    }
  }, [isOpen]);

  const combineDateAndTime = (): moment.Moment => {
    const dateStr = moment(dateDue).format('YYYY-MM-DD');
    const timeMatch = timeDue.match(/^(\d{1,2}):(\d{2})\s?(AM|PM)?$/i);

    let hour = 17;
    let minute = 0;

    if (timeMatch) {
      hour = parseInt(timeMatch[1], 10);
      minute = parseInt(timeMatch[2], 10);
      const period = timeMatch[3];

      if (period) {
        if (period.toUpperCase() === 'PM' && hour !== 12) {
          hour += 12;
        }
        if (period.toUpperCase() === 'AM' && hour === 12) {
          hour = 0;
        }
      }
    }

    return moment.tz(`${dateStr} ${hour}:${minute}`, 'YYYY-MM-DD HH:mm', timezone);
  };

  const validateDateTime = (): boolean => {
    if (!dateDue) {
      setError("Response due date is required");
      return false;
    }

    if (!timeDue) {
      setError("Response due time is required");
      return false;
    }

    const selectedMoment = combineDateAndTime();
    const minMoment = moment().add(24, "hours");

    if (selectedMoment.isBefore(minMoment)) {
      setError("Due date and time must be at least 24 hours from now");
      return false;
    }

    setError("");
    return true;
  };

  const handleConfirm = async () => {
    if (!validateDateTime()) {
      return;
    }

    const dateWithTimezone = combineDateAndTime().toISOString();
    await onConfirm(dateWithTimezone);
  };

  const handleDateChange = (value: Date) => {
    setDateDue(value);
    if (error) {
      setError("");
    }
  };

  const handleTimeChange = (value: string) => {
    setTimeDue(value);
    if (error) {
      setError("");
    }
  };

  const handleTimezoneChange = (value: string) => {
    setTimezone(value);
    if (error) {
      setError("");
    }
  };

  const truncateText = (text: string, maxLength: number) => {
    if (!text) {
      return "";
    }
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  const FooterComponent = () => {
    return (
      <div className="flex w-full justify-between gap-2">
        <Button onClick={onClose} btntype="base" className="!w-full" disabled={isLoading}>
          Cancel
        </Button>
        <Button
          className="!w-full"
          onClick={handleConfirm}
          disabled={isLoading || !!error}
        >
          {isLoading ? <Spinner size="sm" /> : "Send Clarifications"}
        </Button>
      </div>
    );
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      header={{
        icon: "send",
        bgColor: "bg-blue-50",
        iconColor: "text-blue-600",
        title: "Send Clarifications",
        description: `Send ${clarifications.length} clarification${clarifications.length !== 1 ? "s" : ""} to ${supplierName}`,
      }}
      size="lg"
      footerComponent={<FooterComponent />}
    >
      <div className="space-y-4">
        <div className="rounded-lg bg-gray-50 p-4">
          <div className="mb-2 text-sm font-medium text-gray-700">
            Sending {clarifications.length} clarification{clarifications.length !== 1 ? "s" : ""} to:
          </div>
          <div className="mb-3 text-base font-semibold text-gray-900">{supplierName}</div>

          {clarifications.length <= 3 ? (
            <div className="space-y-2">
              {clarifications.map((clarification) => (
                <div key={clarification.id} className="text-sm text-gray-600">
                  <span className="font-medium">#{clarification.reference_number}:</span>{" "}
                  {truncateText(clarification.clarification_text, 100)}
                </div>
              ))}
            </div>
          ) : (
            <div className="max-h-40 space-y-2 overflow-y-auto">
              {clarifications.map((clarification) => (
                <div key={clarification.id} className="text-sm text-gray-600">
                  <span className="font-medium">#{clarification.reference_number}:</span>{" "}
                  {truncateText(clarification.clarification_text, 80)}
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <DateInputWithLabel
            hasError={!!error}
            label="Response Due Date / Time"
            name="date_due"
            required
            value={dateDue}
            onChange={handleDateChange}
            minDate={moment().add(24, "hours").toDate()}
          />
        </div>

        <div>
          <TimePickerWithLabel
            hasError={!!error}
            name="time_due"
            nameTimezone="timezone_due"
            value={timeDue}
            timezone={timezone}
            onTimeChange={handleTimeChange}
            onTimeZoneChange={handleTimezoneChange}
            required
            restrictTimezones="Australia"
          />
        </div>
        <FormikFieldError text={error} />

        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
          <p className="text-sm text-blue-800">
            The supplier will receive these clarifications and will have until{" "}
            <span className="font-semibold">
              {dateDue && timeDue
                ? combineDateAndTime().format("MMMM D, YYYY [at] h:mm A z")
                : "the selected date"}
            </span>{" "}
            to respond.
          </p>
        </div>
      </div>
    </CustomModal>
  );
};
